import React, { useMemo, useState } from 'react';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';

export type MultiSelectOption = {
  id: number;
  label: string;
  subtitle?: string | null;
  /** When true, the option cannot be toggled. */
  disabled?: boolean;
  /** Extra status shown next to selected / locked rows. */
  badge?: string | null;
};

type Props = {
  label: string;
  options: MultiSelectOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  error?: string;
  disabled?: boolean;
  emptyLabel?: string;
  accessibilityLabel?: string;
  /** When true, the option list starts expanded. */
  defaultOpen?: boolean;
};

function selectedLabel(
  options: MultiSelectOption[],
  selectedIds: number[],
  emptyLabel: string,
): string {
  if (!selectedIds.length) return emptyLabel;
  const names = options
    .filter(option => selectedIds.includes(option.id))
    .map(option => option.label)
    .filter(Boolean);
  if (!names.length) return `${selectedIds.length} selected`;
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
}

export function MultiSelectEntityPicker({
  label,
  options,
  selectedIds,
  onChange,
  error,
  disabled,
  emptyLabel = 'Select items',
  accessibilityLabel = 'Select items',
  defaultOpen = false,
}: Props) {
  const palette = useAppColors();
  const [open, setOpen] = useState(defaultOpen);
  const themed = useMemo(
    () => ({
      label: {
        ...typography.caption,
        color: palette.textMuted,
        fontWeight: '600' as const,
      },
      value: { ...typography.input, color: palette.textPrimary },
      placeholder: { ...typography.input, color: palette.textMuted },
      error: { ...typography.caption, color: palette.error },
      optionLabel: { ...typography.body, color: palette.textPrimary },
      optionSubtitle: { ...typography.caption, color: palette.textSecondary },
      badgeSelected: {
        ...typography.caption,
        color: palette.primary,
        fontWeight: '700' as const,
      },
      badgeLocked: {
        ...typography.caption,
        color: palette.textMuted,
        fontWeight: '600' as const,
      },
      field: {
        borderWidth: 1,
        borderColor: error ? palette.error : palette.border,
        backgroundColor: palette.surface,
        borderRadius: 10,
        minHeight: 44,
        paddingHorizontal: spacing.md,
        justifyContent: 'center' as const,
        opacity: disabled ? 0.6 : 1,
      },
    }),
    [disabled, error, palette],
  );

  const summary = selectedLabel(options, selectedIds, emptyLabel);

  function toggle(id: number, optionDisabled?: boolean) {
    if (disabled || optionDisabled) return;
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter(value => value !== id)
        : [...selectedIds, id],
    );
  }

  return (
    <View>
      <Text style={themed.label}>{label}</Text>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        disabled={disabled}
        onPress={() => setOpen(current => !current)}
        style={themed.field}
      >
        <Text
          numberOfLines={3}
          style={selectedIds.length ? themed.value : themed.placeholder}
        >
          {summary}
        </Text>
      </Pressable>
      {error ? <Text style={themed.error}>{error}</Text> : null}
      {open ? (
        <View
          style={[
            styles.listShell,
            { borderColor: palette.border, backgroundColor: palette.surface },
          ]}
        >
          {options.length === 0 ? (
            <Text style={[styles.empty, { color: palette.textSecondary }]}>
              No options available
            </Text>
          ) : (
            <ScrollView
              style={styles.listScroll}
              nestedScrollEnabled
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator
            >
              {options.map(option => {
                const selected = selectedIds.includes(option.id);
                const optionDisabled = Boolean(option.disabled);
                const badge =
                  option.badge ||
                  (selected ? 'Selected' : null) ||
                  (optionDisabled ? 'Unavailable' : null);
                return (
                  <Pressable
                    key={option.id}
                    accessibilityRole="checkbox"
                    accessibilityState={{
                      checked: selected,
                      disabled: optionDisabled,
                    }}
                    disabled={optionDisabled}
                    onPress={() => toggle(option.id, optionDisabled)}
                    style={[
                      styles.option,
                      {
                        borderBottomColor: palette.border,
                        backgroundColor: selected
                          ? `${palette.primary}12`
                          : palette.surface,
                        borderLeftWidth: selected ? 3 : 0,
                        borderLeftColor: selected
                          ? palette.primary
                          : 'transparent',
                        opacity: optionDisabled ? 0.55 : 1,
                      },
                    ]}
                  >
                    <View style={styles.optionHeader}>
                      <Text
                        style={[themed.optionLabel, styles.optionTitle]}
                        numberOfLines={2}
                      >
                        {option.label}
                      </Text>
                      {badge ? (
                        <Text
                          style={
                            selected ? themed.badgeSelected : themed.badgeLocked
                          }
                        >
                          {badge}
                        </Text>
                      ) : null}
                    </View>
                    {option.subtitle ? (
                      <Text style={themed.optionSubtitle}>{option.subtitle}</Text>
                    ) : null}
                  </Pressable>
                );
              })}
            </ScrollView>
          )}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  listShell: {
    marginTop: spacing.sm,
    borderWidth: 1,
    borderRadius: 10,
    overflow: 'hidden',
    height: 320,
  },
  listScroll: {
    height: 320,
    ...(Platform.OS === 'web'
      ? ({ maxHeight: 320, overflow: 'auto' } as object)
      : null),
  },
  option: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: 2,
  },
  optionHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  optionTitle: {
    flex: 1,
  },
  empty: {
    ...typography.caption,
    padding: spacing.md,
  },
});
